import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Typography, Stack, MenuItem, Divider } from '@mui/material';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useSettings from '../../../hooks/useSettings';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import Iconify from '../../../components/Iconify';
import EmployeeProfileModal from './EmployeeProfileModal';
// api
import { getEmployeeByEmail } from '../../../features/empleados/apiEmployees';
import { getPositions } from '../../../features/empleados/positions/apiPositions';

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  padding: theme.spacing(2, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.grey[500_12],
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
}));

const MENU_OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
];

NavbarAccount.propTypes = {
  isCollapse: PropTypes.bool,
};

function getEmployeeDisplayName(employee) {
  const firstName = employee?.first_name?.trim();
  const paternalLastName = employee?.first_last_name?.trim();

  return [firstName, paternalLastName].filter(Boolean).join(' ') || 'Usuario';
}

function getEmployeePosition(employee, positions) {
  const position = employee?.position_type;
  if (position?.name) return position.name;

  const positionId = position?.uuid || position;
  return positions?.find((item) => item.uuid === positionId)?.name || '';
}

function useFittedName(text) {
  const elementRef = useRef(null);
  const [textStyle, setTextStyle] = useState({ fontSize: 14, whiteSpace: 'nowrap' });

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || !text) return undefined;

    let isActive = true;
    const maxFontSize = 14;
    const minFontSize = 11;

    const fitText = () => {
      const availableWidth = element.clientWidth;
      if (!availableWidth) return;

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return;

      const computedStyle = window.getComputedStyle(element);
      context.font = `${computedStyle.fontWeight} ${maxFontSize}px ${computedStyle.fontFamily}`;

      const measuredWidth = context.measureText(text).width;
      const fittedSize = measuredWidth
        ? Math.min(maxFontSize, (availableWidth / measuredWidth) * maxFontSize * 0.98)
        : maxFontSize;
      const nextStyle =
        fittedSize >= minFontSize
          ? { fontSize: Number(fittedSize.toFixed(1)), whiteSpace: 'nowrap' }
          : { fontSize: minFontSize, whiteSpace: 'normal' };

      if (isActive) {
        setTextStyle((current) =>
          current.fontSize === nextStyle.fontSize && current.whiteSpace === nextStyle.whiteSpace ? current : nextStyle
        );
      }
    };

    fitText();
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitText) : null;
    resizeObserver?.observe(element.parentElement || element);
    document.fonts?.ready.then(fitText);

    return () => {
      isActive = false;
      resizeObserver?.disconnect();
    };
  }, [text]);

  return { elementRef, textStyle };
}

export default function NavbarAccount({ isCollapse }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(null);
  useSettings();
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const { data: positionsData } = useQuery({ queryKey: ['positions'], queryFn: getPositions });
  const employeeDisplayName = getEmployeeDisplayName(employeeData);
  const employeePosition = getEmployeePosition(employeeData, positionsData?.results);
  const { elementRef: nameRef, textStyle: nameTextStyle } = useFittedName(employeeDisplayName);

  useEffect(() => {
    let isMounted = true;

    const loadEmployee = async () => {
      if (!user?.email) return;

      setLoadingEmployee(true);
      try {
        const employee = await getEmployeeByEmail(user.email);
        if (isMounted) setEmployeeData(employee || null);
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        if (isMounted) setLoadingEmployee(false);
      }
    };

    loadEmployee();

    return () => {
      isMounted = false;
    };
  }, [user?.email]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleCloseProfile = () => {
    setOpenProfileModal(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <>
      <RootStyle
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          ...(isCollapse && {
            bgcolor: 'transparent',
          }),
        }}
      >
        <MyAvatar sx={{ flexShrink: 0 }} />

        <Box
          sx={{
            ml: 1.25,
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            transition: (theme) =>
              theme.transitions.create('width', {
                duration: theme.transitions.duration.shorter,
              }),
            ...(isCollapse && {
              ml: 0,
              width: 0,
            }),
          }}
        >
          <Typography
            ref={nameRef}
            variant="subtitle2"
            sx={{
              fontSize: `${nameTextStyle.fontSize}px`,
              whiteSpace: nameTextStyle.whiteSpace,
              overflowWrap: 'anywhere',
              lineHeight: 1.25,
            }}
          >
            {employeeDisplayName}
          </Typography>
          {employeePosition && (
            <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
              {employeePosition}
            </Typography>
          )}
        </Box>
        <Iconify
          icon="material-symbols:arrow-drop-down"
          sx={{ ml: 0.5, width: 20, height: 20, flexShrink: 0, ...(isCollapse && { display: 'none' }) }}
        />
      </RootStyle>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {employeeDisplayName}
          </Typography>
          {employeePosition && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {employeePosition}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              disabled={!!option.disabled}
              component={RouterLink}
              onClick={handleClose}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1, color: 'error.main' }}>
          Cerrar Sesión
          <Iconify icon="material-symbols:logout-rounded" sx={{ ml: 1, fontSize: 20 }} />
        </MenuItem>
      </MenuPopover>

      <EmployeeProfileModal
        open={openProfileModal}
        onClose={handleCloseProfile}
        employee={employeeData}
        loading={loadingEmployee}
      />
    </>
  );
}
